/**
 * EPA ID format validation and lookup helpers — a dependency-free leaf module.
 *
 * Kept separate from `helpers.ts` on purpose: `helpers.ts` pulls in the
 * waste-code machinery from `encamp-shared`, which is server-oriented and heavy.
 * Keeping these primitives dependency-free lets light-weight consumers import
 * them without that graph — notably `validationContext.ts` (for the
 * `EpaIdLookupMap` type) and, historically, the Oak client for the pure format
 * check. `helpers.ts` re-exports `isValidEPAId` for backend callers.
 */

/**
 * Validates an EPA ID in the standard format: a 2-letter state/territory code
 * (or "FC" for foreign country) followed by 10 alphanumeric characters, for 12
 * characters total.
 *
 * Per the EPA spec the trailing 10 characters are alphanumeric, NOT strictly
 * "1 letter + 9 digits" — that narrower pattern (e.g. TXD982556581) is common
 * but some handlers carry a pure 2-letter prefix + 10 digits, or trailing
 * letters. We intentionally accept all of these to avoid false rejections.
 *
 * Examples of valid IDs: TXD982556581, AZR000528208, FCD123456789, TX0123456789
 * Examples of invalid IDs: NVCESQG, UNKNOWN, STERICYCLE, 1655MILANDR, 245WLODGEDR
 */
export function isValidEPAId(value: string): boolean {
  if (!value) {
    return false;
  }

  const trimmed = value.trim().toUpperCase();
  if (trimmed.length !== 12) {
    return false;
  }

  // 2-letter state/territory/FC prefix + 10 alphanumeric characters.
  const standardFormat =
    /^(AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY|DC|PR|VI|GU|AS|MP|FC)[A-Z0-9]{10}$/;

  return standardFormat.test(trimmed);
}

/** Canonical form for matching EPA IDs: trimmed and uppercased. */
export function normalizeEpaId(value: string): string {
  return value.trim().toUpperCase();
}

/**
 * The result of looking a single EPA ID up against external/tenant data.
 *  - `registered`: matches a row in the public EPA/RCRAInfo handler table.
 *  - `inTenantFacility`: matches one of the requesting tenant's own facility
 *    EPA IDs (scoped to the tenant — never reveals other tenants).
 *  - `handlerName`: the registered handler's name, when known, for surfacing in
 *    a warning message.
 */
export type EpaIdLookupVerdict = {
  registered: boolean;
  inTenantFacility: boolean;
  handlerName: string | null;
};

/** Lookups injected into the ValidationContext, keyed by `normalizeEpaId(value)`. */
export type EpaIdLookupMap = Map<string, EpaIdLookupVerdict>;

/**
 * Decide the warning message (if any) for one EPA ID given the injected lookups.
 * Pure so it can be shared by the generator/destination/transporter rules.
 *
 *  - Skips malformed IDs (the format rule already flags those) and IDs with no
 *    injected verdict (e.g. callers that didn't pre-fetch lookups).
 *  - An unregistered ID always warns.
 *  - When `requireTenantMatch` (the generator), a registered ID that isn't one
 *    of the tenant's own facilities warns too — surfacing the registered
 *    handler name when known.
 */
export function epaIdLookupWarning(
  value: string | null | undefined,
  lookups: EpaIdLookupMap,
  opts: { requireTenantMatch: boolean }
): string | undefined {
  if (!value || !isValidEPAId(value)) {
    return undefined;
  }

  const verdict = lookups.get(normalizeEpaId(value));
  if (!verdict) {
    return undefined;
  }

  if (!verdict.registered) {
    return "No matching registered EPA ID found.";
  }

  if (opts.requireTenantMatch && !verdict.inTenantFacility) {
    const suffix = verdict.handlerName ? ` (registered to ${verdict.handlerName})` : "";
    return `This EPA ID doesn't match any of your facilities${suffix}.`;
  }

  return undefined;
}
